import { useEnv } from '@sparklayer-io/spark-dash-core/context/env-context';
import { SalesPerformanceCard } from '@sparklayer-io/spark-dash-pages';
import {
  useHandlers,
  useUser,
  withPageProviders,
} from '@sparklayer-io/spark-dash-pages/context/page-context';
import { Card, Page, ProgressBar } from '@ui/themed';
import { t } from 'i18next';
import type { I18nPrefix } from '../../../i18n/prefix';
import { Onboarding } from '../components/onboarding/onboarding';
import { OnboardingActions } from '../components/onboarding-actions';
import { PlanReminders } from '../components/plan-reminders';
import { StatusBanner } from '../components/status-banner';
import { UpsellPlan } from '../components/upsell-plan';
import { HELP_PATH, PRODUCT_TOUR_ID } from '../constants';
import {
  isOnboardingComplete,
  onboardingStepKeys,
  onboardingStepsCompleted,
} from '../lib/onboarding-steps';

const PAGE = 'features:home.pages.home_page' satisfies I18nPrefix;

export const HomePage = withPageProviders(HomePageContent);

function HomePageContent() {
  const env = useEnv();
  const { firstName } = useUser();
  const { navigationHandler } = useHandlers();

  const completed = isOnboardingComplete(env);
  const steps = onboardingStepKeys(env);
  const stepsCompleted = onboardingStepsCompleted(env);

  return (
    <Page
      title={t(completed ? `${PAGE}.header.title_complete` : `${PAGE}.header.title_incomplete`, {
        firstName,
      })}
      primaryAction={
        completed
          ? undefined
          : {
              label: t(`${PAGE}.header.setup_assistance`),
              icon: 'checkCircle',
              onClick: () =>
                navigationHandler.navigate(HELP_PATH, {
                  excludeSiteId: true,
                  excludeSiteEnv: true,
                }),
            }
      }
      secondaryActions={
        completed
          ? undefined
          : [
              {
                label: t(`${PAGE}.header.watch_tour`),
                icon: 'playCircle',
                hideOnMobile: true,
                // Only a full page load hands the tour ID to Intercom.
                onClick: () =>
                  navigationHandler.navigate('/', {
                    absolute: true,
                    query: { product_tour_id: PRODUCT_TOUR_ID },
                  }),
              },
            ]
      }
    >
      <PlanReminders />

      {!completed ? (
        <>
          <Card>
            <Card.Header headingLevel="h2">{t(`${PAGE}.onboarding_progress.title`)}</Card.Header>
            <Card.Body>
              <ProgressBar caption size="lg" segments={steps.length} filled={stepsCompleted} />
            </Card.Body>
          </Card>
          <Onboarding />
          <OnboardingActions />
        </>
      ) : (
        <StatusBanner
          tone="green"
          icon="checkCircle"
          title={t(`${PAGE}.all_set.title`)}
          description={t(`${PAGE}.all_set.description`)}
          onClick={() => navigationHandler.navigate('/configuration/onboarding')}
        />
      )}

      <SalesPerformanceCard allowCustomRange />

      <UpsellPlan />
    </Page>
  );
}
